'use client';

import React, { useState, useMemo } from 'react';
import { Search, ExternalLink, Eye, Trash2, ChevronUp, ChevronDown, ChevronsUpDown, CheckSquare, Square, Film, FileText, X, ChevronLeft, ChevronRight, Send, Loader2 } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';

type ScriptStatus = 'draft' | 'ready' | 'rendering' | 'queued' | 'published';
type RenderStatus = 'pending' | 'processing' | 'complete' | 'failed' | 'na';

interface Script {
  id: string;
  title: string;
  hook: string;
  sourceSignal: string;
  sourcePlatform: 'reddit' | 'hn' | 'producthunt';
  status: ScriptStatus;
  wordCount: number;
  createdAt: string;
  renderStatus: RenderStatus;
  renderProgress: number;
  captionLength: number;
  hashtags: number;
  studioUrl: string;
  videoUrl?: string;
  caption?: string;
}

const scripts: Script[] = [
  {
    id: 'script-001',
    title: 'Claude 3.5 Sonnet vs GPT-4o: Which AI Writes Better Code?',
    hook: 'You\'ve been using the wrong AI for coding — here\'s what the benchmarks actually say',
    sourceSignal: 'claude-sonnet-coding',
    sourcePlatform: 'reddit',
    status: 'published',
    wordCount: 142,
    createdAt: '2026-07-30 07:31',
    renderStatus: 'complete',
    renderProgress: 100,
    captionLength: 287,
    hashtags: 12,
    studioUrl: 'https://studio.contentpipeline.ai/script-001',
    videoUrl: 'https://storage.contentpipeline.ai/reels/script-001.mp4',
    caption: 'You\'ve been using the wrong AI for coding 🤯 Here\'s what the benchmarks actually say about Claude 3.5 Sonnet vs GPT-4o. #AI #CodingTools #Claude #GPT4o #ArtificialIntelligence #MachineLearning #Developer #TechTips #AITools #Coding #Programming #LLM',
  },
  {
    id: 'script-002',
    title: 'OpenAI o3 Reasoning Model Benchmarks Explained in 50s',
    hook: 'OpenAI just dropped a reasoning model that outperforms PhD-level humans on math',
    sourceSignal: 'openai-o3-release',
    sourcePlatform: 'hn',
    status: 'published',
    wordCount: 138,
    createdAt: '2026-07-29 07:32',
    renderStatus: 'complete',
    renderProgress: 100,
    captionLength: 312,
    hashtags: 14,
    studioUrl: 'https://studio.contentpipeline.ai/script-002',
    videoUrl: 'https://storage.contentpipeline.ai/reels/script-002.mp4',
    caption: 'OpenAI just dropped a reasoning model that outperforms PhD-level humans on math 🧠 #OpenAI #o3 #AI #ReasoningAI #MachineLearning #ArtificialIntelligence #TechNews #AIResearch #LLM #GPT #FutureOfAI #AIBenchmarks',
  },
  {
    id: 'script-003',
    title: 'Perplexity AI Deep Research vs Gemini: 3 Key Differences',
    hook: 'Deep Research just made Google Scholar feel like a 2019 product',
    sourceSignal: 'perplexity-deep-research',
    sourcePlatform: 'reddit',
    status: 'published',
    wordCount: 145,
    createdAt: '2026-07-28 07:30',
    renderStatus: 'complete',
    renderProgress: 100,
    captionLength: 298,
    hashtags: 11,
    studioUrl: 'https://studio.contentpipeline.ai/script-003',
    videoUrl: 'https://storage.contentpipeline.ai/reels/script-003.mp4',
    caption: 'Deep Research just made Google Scholar feel like a 2019 product 🔍 #Perplexity #Gemini #AIResearch #DeepResearch #AI #TechTools #ArtificialIntelligence #ResearchTools #LLM #AIComparison',
  },
  {
    id: 'script-004',
    title: 'Anthropic Constitutional AI — What It Actually Means',
    hook: 'Anthropic trained Claude to refuse its own creators — here\'s how Constitutional AI works',
    sourceSignal: 'anthropic-constitutional-ai',
    sourcePlatform: 'hn',
    status: 'queued',
    wordCount: 151,
    createdAt: '2026-07-30 07:34',
    renderStatus: 'complete',
    renderProgress: 100,
    captionLength: 341,
    hashtags: 13,
    studioUrl: 'https://studio.contentpipeline.ai/script-004',
    videoUrl: 'https://storage.contentpipeline.ai/reels/script-004.mp4',
    caption: 'Anthropic trained Claude to refuse its own creators 🤖 Here\'s how Constitutional AI actually works. #Anthropic #Claude #ConstitutionalAI #AISafety #AI #MachineLearning #ArtificialIntelligence #TechExplained #LLM #AIEthics',
  },
  {
    id: 'script-005',
    title: 'LLM Hallucination Rate Drop: New RLHF Technique Breakdown',
    hook: 'Researchers cut LLM hallucinations by 41% with one training trick — here\'s exactly how',
    sourceSignal: 'rlhf-hallucination-reduction',
    sourcePlatform: 'reddit',
    status: 'rendering',
    wordCount: 147,
    createdAt: '2026-07-30 07:35',
    renderStatus: 'processing',
    renderProgress: 68,
    captionLength: 0,
    hashtags: 0,
    studioUrl: 'https://studio.contentpipeline.ai/script-005',
  },
  {
    id: 'script-006',
    title: 'Mistral 7B vs Llama 3.1: Edge Deployment Comparison',
    hook: 'Running AI locally on your laptop is now actually possible — Mistral 7B vs Llama 3.1',
    sourceSignal: 'mistral-llama-edge',
    sourcePlatform: 'producthunt',
    status: 'queued',
    wordCount: 139,
    createdAt: '2026-07-30 07:36',
    renderStatus: 'complete',
    renderProgress: 100,
    captionLength: 275,
    hashtags: 10,
    studioUrl: 'https://studio.contentpipeline.ai/script-006',
    videoUrl: 'https://storage.contentpipeline.ai/reels/script-006.mp4',
    caption: 'Running AI locally on your laptop is now actually possible 💻 Mistral 7B vs Llama 3.1 edge deployment comparison. #Mistral #Llama #EdgeAI #LocalAI #AI #MachineLearning #OpenSource #LLM #TechTools #AIDeployment',
  },
  {
    id: 'script-007',
    title: 'Google Gemini 2.0 Flash: Real-Time Multimodal Demo',
    hook: 'Google\'s new Gemini processes video, audio, and text simultaneously — watch this live demo',
    sourceSignal: 'gemini-2-flash-demo',
    sourcePlatform: 'hn',
    status: 'ready',
    wordCount: 144,
    createdAt: '2026-07-30 07:38',
    renderStatus: 'pending',
    renderProgress: 0,
    captionLength: 318,
    hashtags: 15,
    studioUrl: 'https://studio.contentpipeline.ai/script-007',
    videoUrl: 'https://storage.contentpipeline.ai/reels/script-007.mp4',
    caption: 'Google\'s new Gemini processes video, audio, and text simultaneously 🚀 Watch this live demo. #Gemini #Google #MultimodalAI #AI #TechDemo #ArtificialIntelligence #MachineLearning #GeminiFlash #LLM #AITools #TechNews #GoogleAI',
  },
  {
    id: 'script-008',
    title: 'AI Agents in 2026: From Demos to Production Deployments',
    hook: '84% of AI agent demos never reach production — here\'s the 3 reasons why yours will',
    sourceSignal: 'ai-agents-production-2026',
    sourcePlatform: 'reddit',
    status: 'ready',
    wordCount: 153,
    createdAt: '2026-07-30 07:40',
    renderStatus: 'pending',
    renderProgress: 0,
    captionLength: 302,
    hashtags: 12,
    studioUrl: 'https://studio.contentpipeline.ai/script-008',
    videoUrl: 'https://storage.contentpipeline.ai/reels/script-008.mp4',
    caption: '84% of AI agent demos never reach production 📊 Here\'s the 3 reasons why yours will. #AIAgents #AI #Production #MachineLearning #ArtificialIntelligence #TechTips #LLM #AgentAI #Automation #AIEngineering',
  },
  {
    id: 'script-009',
    title: 'Fine-Tuning vs RAG: When Each Approach Actually Wins',
    hook: 'Stop fine-tuning when you should be using RAG — the decision framework in 50 seconds',
    sourceSignal: 'finetuning-vs-rag',
    sourcePlatform: 'hn',
    status: 'ready',
    wordCount: 148,
    createdAt: '2026-07-30 08:12',
    renderStatus: 'pending',
    renderProgress: 0,
    captionLength: 289,
    hashtags: 11,
    studioUrl: 'https://studio.contentpipeline.ai/script-009',
    videoUrl: 'https://storage.contentpipeline.ai/reels/script-009.mp4',
    caption: 'Stop fine-tuning when you should be using RAG 🧠 The decision framework in 50 seconds. #FineTuning #RAG #LLM #AI #MachineLearning #ArtificialIntelligence #AIEngineering #NLP #TechTips #AITools',
  },
  {
    id: 'script-010',
    title: 'Cursor AI vs GitHub Copilot: Developer Productivity in 2026',
    hook: 'I switched from Copilot to Cursor for 30 days — here\'s what the data actually shows',
    sourceSignal: 'cursor-vs-copilot-2026',
    sourcePlatform: 'reddit',
    status: 'draft',
    wordCount: 0,
    createdAt: '2026-07-30 11:58',
    renderStatus: 'na',
    renderProgress: 0,
    captionLength: 0,
    hashtags: 0,
    studioUrl: 'https://studio.contentpipeline.ai/script-010',
  },
  {
    id: 'script-011',
    title: 'Stable Diffusion 3.5 vs DALL-E 3: Image Quality Breakdown',
    hook: 'SD 3.5 just matched DALL-E 3 on photorealism — at 1/10th the API cost',
    sourceSignal: 'sd35-dalle3-comparison',
    sourcePlatform: 'producthunt',
    status: 'draft',
    wordCount: 0,
    createdAt: '2026-07-30 12:01',
    renderStatus: 'na',
    renderProgress: 0,
    captionLength: 0,
    hashtags: 0,
    studioUrl: 'https://studio.contentpipeline.ai/script-011',
  },
  {
    id: 'script-012',
    title: 'Function Calling in LLMs: The Tool Use Pattern Explained',
    hook: 'Function calling is how AI agents actually take actions — here\'s the exact pattern',
    sourceSignal: 'llm-function-calling-tools',
    sourcePlatform: 'hn',
    status: 'ready',
    wordCount: 141,
    createdAt: '2026-07-30 09:22',
    renderStatus: 'pending',
    renderProgress: 0,
    captionLength: 295,
    hashtags: 10,
    studioUrl: 'https://studio.contentpipeline.ai/script-012',
    videoUrl: 'https://storage.contentpipeline.ai/reels/script-012.mp4',
    caption: 'Function calling is how AI agents actually take actions 🔧 Here\'s the exact pattern explained. #FunctionCalling #LLM #AIAgents #AI #MachineLearning #ArtificialIntelligence #TechExplained #AIEngineering #Tools #Automation',
  },
];

const platformBadge: Record<Script['sourcePlatform'], { label: string; className: string }> = {
  reddit: { label: 'Reddit', className: 'bg-orange-500/10 text-orange-400 border border-orange-500/20' },
  hn: { label: 'HN', className: 'bg-amber-500/10 text-amber-400 border border-amber-500/20' },
  producthunt: { label: 'PH', className: 'bg-red-500/10 text-red-400 border border-red-500/20' },
};

const renderStatusLabel: Record<RenderStatus, string> = {
  pending: 'Pending',
  processing: 'Processing',
  complete: 'Done',
  failed: 'Failed',
  na: '—',
};

const renderStatusColor: Record<RenderStatus, string> = {
  pending: 'text-muted-foreground',
  processing: 'text-info',
  complete: 'text-positive',
  failed: 'text-negative',
  na: 'text-muted-foreground',
};

const STATUS_FILTERS: { label: string; value: ScriptStatus | 'all' }[] = [
  { label: 'All', value: 'all' },
  { label: 'Draft', value: 'draft' },
  { label: 'Ready', value: 'ready' },
  { label: 'Rendering', value: 'rendering' },
  { label: 'Queued', value: 'queued' },
  { label: 'Published', value: 'published' },
];

type SortField = 'title' | 'status' | 'wordCount' | 'createdAt' | 'renderStatus';
type SortDir = 'asc' | 'desc' | null;

export default function ScriptsTable() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<ScriptStatus | 'all'>('all');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [sortField, setSortField] = useState<SortField>('createdAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [hoveredRow, setHoveredRow] = useState<string | null>(null);
  const [tooltipScript, setTooltipScript] = useState<string | null>(null);
  const [publishingIds, setPublishingIds] = useState<Set<string>>(new Set());
  const [publishedIds, setPublishedIds] = useState<Set<string>>(new Set());
  const [publishErrors, setPublishErrors] = useState<Record<string, string>>({});
  const [publishingAll, setPublishingAll] = useState(false);

  const filtered = useMemo(() => {
    let result = [...scripts];
    if (search) {
      const q = search.toLowerCase();
      result = result.filter(
        (s) =>
          s.title.toLowerCase().includes(q) ||
          s.sourceSignal.toLowerCase().includes(q) ||
          s.hook.toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'all') {
      result = result.filter((s) => s.status === statusFilter);
    }
    if (sortField && sortDir) {
      result.sort((a, b) => {
        let av: string | number = a[sortField] as string | number;
        let bv: string | number = b[sortField] as string | number;
        if (typeof av === 'string') av = av.toLowerCase();
        if (typeof bv === 'string') bv = bv.toLowerCase();
        if (av < bv) return sortDir === 'asc' ? -1 : 1;
        if (av > bv) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return result;
  }, [search, statusFilter, sortField, sortDir]);

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : d === 'desc' ? null : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const toggleAll = () => {
    if (selected.size === paginated.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(paginated.map((s) => s.id)));
    }
  };

  const toggleRow = (id: string) => {
    const next = new Set(selected);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelected(next);
  };

  const handlePublish = async (script: Script) => {
    if (!script.videoUrl || !script.caption) {
      setPublishErrors((prev) => ({
        ...prev,
        [script.id]: 'Missing videoUrl or caption — cannot publish',
      }));
      return;
    }

    setPublishingIds((prev) => new Set(prev).add(script.id));
    setPublishErrors((prev) => {
      const next = { ...prev };
      delete next[script.id];
      return next;
    });

    try {
      const res = await fetch('/api/meta/publish', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          videoUrl: script.videoUrl,
          caption: script.caption,
          shareToFeed: true,
          platforms: ['instagram'],
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error ?? `HTTP ${res.status}`);
      }

      setPublishedIds((prev) => new Set(prev).add(script.id));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Publish failed';
      setPublishErrors((prev) => ({ ...prev, [script.id]: message }));
    } finally {
      setPublishingIds((prev) => {
        const next = new Set(prev);
        next.delete(script.id);
        return next;
      });
    }
  };

  const canPublish = (script: Script) =>
    (script.status === 'ready' || script.status === 'queued') &&
    !!script.videoUrl &&
    !!script.caption;

  // Publish all past/queued/ready scripts sequentially
  const handlePublishAll = async () => {
    const publishable = scripts.filter(
      (s) => canPublish(s) && !publishedIds.has(s.id) && !publishingIds.has(s.id)
    );
    if (publishable.length === 0) return;
    setPublishingAll(true);
    for (const script of publishable) {
      await handlePublish(script);
      await new Promise((r) => setTimeout(r, 2000));
    }
    setPublishingAll(false);
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronsUpDown size={12} className="text-muted-foreground/50" />;
    if (sortDir === 'asc') return <ChevronUp size={12} className="text-primary" />;
    if (sortDir === 'desc') return <ChevronDown size={12} className="text-primary" />;
    return <ChevronsUpDown size={12} className="text-muted-foreground/50" />;
  };

  return (
    <div className="card-elevated flex flex-col">
      {/* Toolbar */}
      <div className="p-4 border-b border-border flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search scripts, signals, hooks..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-8 pr-3 py-2 bg-input border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          />
        </div>

        {/* Status filter chips */}
        <div className="flex flex-wrap gap-1.5">
          {STATUS_FILTERS.map((f) => (
            <button
              key={`filter-${f.value}`}
              onClick={() => { setStatusFilter(f.value); setPage(1); }}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-all duration-150 ${
                statusFilter === f.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary border border-border text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              {f.label}
              {f.value !== 'all' && (
                <span className="ml-1 opacity-60">
                  {scripts.filter((s) => s.status === f.value).length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Publish All Past Videos button */}
        {scripts.some((s) => canPublish(s) && !publishedIds.has(s.id)) && (
          <button
            onClick={handlePublishAll}
            disabled={publishingAll}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20 transition-all disabled:opacity-50 whitespace-nowrap"
            title="Publish all queued and ready reels to Instagram at 9:30 AM IST"
          >
            {publishingAll ? (
              <><Loader2 size={12} className="animate-spin" />Publishing All…</>
            ) : (
              <><Send size={12} />Publish All Past Videos · 9:30AM IST</>
            )}
          </button>
        )}
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="px-4 py-2.5 bg-primary/10 border-b border-primary/20 flex items-center gap-3 slide-up">
          <span className="text-xs text-primary font-semibold font-mono">
            {selected.size} selected
          </span>
          <button className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors">
            <Trash2 size={12} />
            Delete
          </button>
          <button className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 transition-colors">
            <Film size={12} />
            Queue for Render
          </button>
          <button
            onClick={() => setSelected(new Set())}
            className="ml-auto text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="p-3 w-10">
                <button onClick={toggleAll} className="text-muted-foreground hover:text-foreground transition-colors">
                  {selected.size === paginated.length && paginated.length > 0 ? (
                    <CheckSquare size={15} className="text-primary" />
                  ) : (
                    <Square size={15} />
                  )}
                </button>
              </th>
              <th className="p-3 text-left">
                <button
                  onClick={() => toggleSort('title')}
                  className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
                >
                  Script Title <SortIcon field="title" />
                </button>
              </th>
              <th className="p-3 text-left whitespace-nowrap">
                <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Source
                </span>
              </th>
              <th className="p-3 text-left">
                <button
                  onClick={() => toggleSort('status')}
                  className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
                >
                  Status <SortIcon field="status" />
                </button>
              </th>
              <th className="p-3 text-left whitespace-nowrap">
                <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Hook Preview
                </span>
              </th>
              <th className="p-3 text-right">
                <button
                  onClick={() => toggleSort('wordCount')}
                  className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
                >
                  Words <SortIcon field="wordCount" />
                </button>
              </th>
              <th className="p-3 text-left whitespace-nowrap">
                <button
                  onClick={() => toggleSort('createdAt')}
                  className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
                >
                  Created <SortIcon field="createdAt" />
                </button>
              </th>
              <th className="p-3 text-left whitespace-nowrap">
                <button
                  onClick={() => toggleSort('renderStatus')}
                  className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-muted-foreground hover:text-foreground transition-colors"
                >
                  Render <SortIcon field="renderStatus" />
                </button>
              </th>
              <th className="p-3 text-right whitespace-nowrap">
                <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Caption
                </span>
              </th>
              <th className="p-3 text-center whitespace-nowrap">
                <span className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Actions
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {paginated.length === 0 ? (
              <tr>
                <td colSpan={10} className="py-16 text-center">
                  <div className="flex flex-col items-center gap-3">
                    <FileText size={32} className="text-muted-foreground/30" />
                    <p className="text-sm font-medium text-muted-foreground">No scripts found</p>
                    <p className="text-xs text-muted-foreground/60">
                      Try adjusting your search or filter to find what you need.
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              paginated.map((script, idx) => {
                const pb = platformBadge[script.sourcePlatform];
                const isSelected = selected.has(script.id);
                const isHovered = hoveredRow === script.id;
                const showTooltip = tooltipScript === script.id;
                const isPublishing = publishingIds.has(script.id);
                const isPublishedNow = publishedIds.has(script.id);
                const publishError = publishErrors[script.id];

                return (
                  <tr
                    key={script.id}
                    onMouseEnter={() => setHoveredRow(script.id)}
                    onMouseLeave={() => setHoveredRow(null)}
                    className={`border-b border-border/50 transition-colors duration-100 ${
                      isSelected ? 'bg-primary/5' : isHovered ? 'bg-muted/30' : idx % 2 === 0 ? '' : 'bg-secondary/30'
                    }`}
                  >
                    {/* Checkbox */}
                    <td className="p-3">
                      <button
                        onClick={() => toggleRow(script.id)}
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {isSelected ? (
                          <CheckSquare size={15} className="text-primary" />
                        ) : (
                          <Square size={15} />
                        )}
                      </button>
                    </td>

                    {/* Title */}
                    <td className="p-3 max-w-[200px]">
                      <p className="text-xs font-medium text-foreground line-clamp-2 leading-snug">
                        {script.title}
                      </p>
                      <p className="text-2xs text-muted-foreground font-mono mt-0.5">{script.id}</p>
                    </td>

                    {/* Source */}
                    <td className="p-3">
                      <div className="flex flex-col gap-1">
                        <span
                          className={`inline-flex items-center px-1.5 py-0.5 rounded text-2xs font-semibold font-mono ${pb.className}`}
                        >
                          {pb.label}
                        </span>
                        <span className="text-2xs text-muted-foreground font-mono truncate max-w-[100px]">
                          {script.sourceSignal}
                        </span>
                      </div>
                    </td>

                    {/* Status */}
                    <td className="p-3">
                      <StatusBadge variant={isPublishedNow ? 'published' : script.status} />
                    </td>

                    {/* Hook Preview */}
                    <td className="p-3 max-w-[200px] relative">
                      {script.hook ? (
                        <div className="relative">
                          <p
                            className="text-xs text-muted-foreground truncate cursor-help"
                            onMouseEnter={() => setTooltipScript(script.id)}
                            onMouseLeave={() => setTooltipScript(null)}
                          >
                            {script.hook}
                          </p>
                          {showTooltip && (
                            <div className="absolute left-0 top-full mt-1 z-50 w-72 p-3 bg-card border border-border rounded-lg shadow-xl fade-in">
                              <p className="text-xs text-muted-foreground mb-1 font-semibold">Hook</p>
                              <p className="text-xs text-foreground leading-relaxed">{script.hook}</p>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-2xs text-muted-foreground/50 font-mono">—</span>
                      )}
                    </td>

                    {/* Word Count */}
                    <td className="p-3 text-right">
                      <span className="text-xs font-mono tabular-nums text-foreground">
                        {script.wordCount > 0 ? script.wordCount : '—'}
                      </span>
                    </td>

                    {/* Created */}
                    <td className="p-3 whitespace-nowrap">
                      <span className="text-xs font-mono text-muted-foreground">{script.createdAt}</span>
                    </td>

                    {/* Render Status */}
                    <td className="p-3">
                      <div className="flex flex-col gap-1">
                        <span
                          className={`text-xs font-mono font-semibold ${renderStatusColor[script.renderStatus]}`}
                        >
                          {renderStatusLabel[script.renderStatus]}
                        </span>
                        {script.renderStatus === 'processing' && (
                          <div className="w-16 h-1 rounded-full bg-muted overflow-hidden">
                            <div
                              className="h-full rounded-full bg-info"
                              style={{ width: `${script.renderProgress}%` }}
                            />
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Caption Length */}
                    <td className="p-3 text-right">
                      <span className="text-xs font-mono tabular-nums text-muted-foreground">
                        {script.captionLength > 0 ? `${script.captionLength}c` : '—'}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="p-3">
                      <div className="flex flex-col gap-1.5">
                        <div
                          className={`flex items-center justify-center gap-1 transition-opacity duration-150 ${
                            isHovered ? 'opacity-100' : 'opacity-0'
                          }`}
                        >
                          <a
                            href={script.studioUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Open in Studio"
                            className="p-1.5 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-all duration-150"
                          >
                            <ExternalLink size={13} />
                          </a>
                          <button
                            title="Preview script"
                            className="p-1.5 rounded-md text-muted-foreground hover:text-accent hover:bg-accent/10 transition-all duration-150"
                          >
                            <Eye size={13} />
                          </button>
                          <button
                            title="Delete script — this cannot be undone"
                            className="p-1.5 rounded-md text-muted-foreground hover:text-negative hover:bg-negative/10 transition-all duration-150"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>

                        {/* Publish to Instagram button — only for ready/queued scripts with videoUrl */}
                        {canPublish(script) && !isPublishedNow && (
                          <button
                            onClick={() => handlePublish(script)}
                            disabled={isPublishing}
                            title="Publish Reel to Instagram — scheduled for 9:30 AM IST"
                            className={`flex items-center justify-center gap-1 px-2 py-1 rounded-md text-2xs font-semibold transition-all duration-150 whitespace-nowrap ${
                              isPublishing
                                ? 'bg-primary/10 text-primary cursor-not-allowed' :'bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20'
                            }`}
                          >
                            {isPublishing ? (
                              <>
                                <Loader2 size={10} className="animate-spin" />
                                Publishing…
                              </>
                            ) : (
                              <>
                                <Send size={10} />
                                Publish IG · 9:30AM IST
                              </>
                            )}
                          </button>
                        )}

                        {/* Success state */}
                        {isPublishedNow && (
                          <span className="flex items-center justify-center gap-1 px-2 py-1 rounded-md text-2xs font-semibold bg-positive/10 text-positive border border-positive/20" title="Scheduled to publish at 9:30 AM IST">
                            ✓ Scheduled 9:30AM IST
                          </span>
                        )}

                        {/* Error state */}
                        {publishError && (
                          <p
                            className="text-2xs text-negative leading-tight max-w-[120px] truncate"
                            title={publishError}
                          >
                            {publishError}
                          </p>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="p-4 border-t border-border flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>Show</span>
          <select
            value={perPage}
            onChange={(e) => { setPerPage(Number(e.target.value)); setPage(1); }}
            className="bg-input border border-border rounded px-2 py-1 text-foreground text-xs focus:outline-none focus:ring-1 focus:ring-ring"
          >
            {[5, 10, 20, 50].map((n) => (
              <option key={`perpage-${n}`} value={n}>{n}</option>
            ))}
          </select>
          <span>
            of <span className="text-foreground font-semibold tabular-nums">{filtered.length}</span> scripts
          </span>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <ChevronLeft size={14} />
          </button>
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
            <button
              key={`page-${p}`}
              onClick={() => setPage(p)}
              className={`w-7 h-7 rounded-md text-xs font-mono transition-all duration-150 ${
                page === p
                  ? 'bg-primary text-primary-foreground font-semibold'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              {p}
            </button>
          ))}
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}